import { useState, useCallback, useRef } from 'react';
import { Recording } from '../components/dashboard/interfaces/Recording';
import { requestWithAuthorization } from '../network/apiRequests';

interface PaginatedUserRecordingsResponse {
  total: number;
  limit: number;
  offset: number;
  recordings: Recording[];
}

interface CacheEntry {
  data: Recording[];
  total: number;
  timestamp: number;
  isLoading: boolean;
  error: string | null;
}

interface CacheKey {
  userId: string;
  limit: number;
  offset: number;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const MAX_CACHE_SIZE = 50; // Maximum number of cache entries

export function useRecordingsCache() {
  const cache = useRef<Map<string, CacheEntry>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateCacheKey = useCallback(({ userId, limit, offset }: CacheKey): string => {
    return `${userId}-${limit}-${offset}`;
  }, []);

  const isCacheValid = useCallback((entry: CacheEntry): boolean => {
    return Date.now() - entry.timestamp < CACHE_DURATION;
  }, []);

  const cleanCache = useCallback(() => {
    const now = Date.now();
    const entries = Array.from(cache.current.entries());
    
    // Remove expired entries
    entries.forEach(([key, entry]) => {
      if (now - entry.timestamp >= CACHE_DURATION) {
        cache.current.delete(key);
      }
    });

    // If still too many entries, remove oldest ones
    if (cache.current.size > MAX_CACHE_SIZE) {
      const sortedEntries = Array.from(cache.current.entries())
        .sort(([, a], [, b]) => a.timestamp - b.timestamp);
      
      const entriesToRemove = sortedEntries.slice(0, cache.current.size - MAX_CACHE_SIZE);
      entriesToRemove.forEach(([key]) => cache.current.delete(key));
    }
  }, []);

  const fetchRecordings = useCallback(async ({ userId, limit, offset }: CacheKey): Promise<{
    recordings: Recording[];
    total: number;
    fromCache: boolean;
  }> => {
    const cacheKey = generateCacheKey({ userId, limit, offset });
    const cachedEntry = cache.current.get(cacheKey);

    // Return cached data if valid and not loading
    if (cachedEntry && isCacheValid(cachedEntry) && !cachedEntry.isLoading) {
      if (cachedEntry.error) {
        setError(cachedEntry.error);
        return { recordings: [], total: 0, fromCache: true };
      }
      setError(null);
      return { 
        recordings: cachedEntry.data, 
        total: cachedEntry.total, 
        fromCache: true 
      };
    }

    // If already loading, wait for the existing request
    if (cachedEntry?.isLoading) {
      return new Promise((resolve) => {
        const checkCache = () => {
          const entry = cache.current.get(cacheKey);
          if (entry && !entry.isLoading) {
            if (entry.error) {
              setError(entry.error);
              resolve({ recordings: [], total: 0, fromCache: true });
            } else {
              setError(null);
              resolve({ 
                recordings: entry.data, 
                total: entry.total, 
                fromCache: true 
              });
            }
          } else {
            setTimeout(checkCache, 100);
          }
        };
        checkCache();
      });
    }

    // Set loading state
    setIsLoading(true);
    setError(null);
    cache.current.set(cacheKey, {
      data: cachedEntry?.data || [],
      total: cachedEntry?.total || 0,
      timestamp: Date.now(),
      isLoading: true,
      error: null
    });

    try {
      const recordingsResponse = await requestWithAuthorization(
        `${process.env.REACT_APP_API_HOSTNAME}/${userId}/recordings?limit=${limit}&offset=${offset}`
      );

      if (recordingsResponse.status !== 200) {
        throw new Error(`HTTP ${recordingsResponse.status}: Failed to fetch recordings`);
      }

      let recordings: Recording[];
      let total: number;

      // Handle both array and paginated response formats
      if (Array.isArray(recordingsResponse.data)) {
        recordings = recordingsResponse.data;
        total = recordingsResponse.data.length;
      } else {
        const paginatedData = recordingsResponse.data as PaginatedUserRecordingsResponse;
        recordings = paginatedData.recordings;
        total = paginatedData.total;
      }

      // Update cache with successful response
      cache.current.set(cacheKey, {
        data: recordings,
        total: total,
        timestamp: Date.now(),
        isLoading: false,
        error: null
      });

      // Clean old cache entries
      cleanCache();

      setIsLoading(false);
      setError(null);

      return { recordings, total, fromCache: false };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch recordings';
      
      // Update cache with error
      cache.current.set(cacheKey, {
        data: cachedEntry?.data || [],
        total: cachedEntry?.total || 0,
        timestamp: Date.now(),
        isLoading: false,
        error: errorMessage
      });

      setIsLoading(false);
      setError(errorMessage);
      
      // Return cached data if available, otherwise empty
      return { 
        recordings: cachedEntry?.data || [], 
        total: cachedEntry?.total || 0, 
        fromCache: true 
      };
    }
  }, [generateCacheKey, isCacheValid, cleanCache]);

  const invalidateCache = useCallback((userId?: string) => {
    if (userId) {
      // Invalidate cache entries for specific user
      const keysToDelete = Array.from(cache.current.keys())
        .filter(key => key.startsWith(userId));
      keysToDelete.forEach(key => cache.current.delete(key));
    } else {
      // Clear entire cache
      cache.current.clear();
    }
  }, []);

  const getCacheInfo = useCallback(() => {
    const entries = Array.from(cache.current.entries());
    return {
      size: cache.current.size,
      entries: entries.map(([key, entry]) => ({
        key,
        recordingsCount: entry.data.length,
        age: Date.now() - entry.timestamp,
        isValid: isCacheValid(entry),
        isLoading: entry.isLoading,
        error: entry.error
      }))
    };
  }, [isCacheValid]);

  return {
    fetchRecordings,
    invalidateCache,
    getCacheInfo,
    isLoading,
    error
  };
}